import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Schedule as ScheduleIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { User } from '../../types';

interface UserViewDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
}

const UserViewDialog: React.FC<UserViewDialogProps> = ({
  open,
  user,
  onClose,
}) => {
  console.log('user', user);
  if (!user) return null;

  const getUserTypeColor = (userType: string | undefined) => {
    switch (userType) {
      case 'Admin':
        return { bg: '#dbeafe', color: '#1d4ed8' };
      case 'Staff':
        return { bg: '#fef3c7', color: '#d97706' };
      case 'Exhibitor':
        return { bg: '#dcfce7', color: '#16a34a' };
      case 'Delegate':
        return { bg: '#fef3c7', color: '#d97706' };
      case 'Speaker':
        return { bg: '#f3e8ff', color: '#7c3aed' };
      case 'Visitor':
        return { bg: '#e0e7ff', color: '#3730a3' };
      default:
        return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? { bg: '#dcfce7', color: '#16a34a' }
      : { bg: '#fee2e2', color: '#dc2626' };
  };

  const userTypeStyle = getUserTypeColor(
    typeof user.userType === 'string' ? user.userType : user.userType?.typeName
  );
  const statusStyle = getStatusColor(user.isActive);

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const phoneNumber =
    user.delegateDetails?.mobileNumber || user.phone || 'Not Provided';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
          <Box>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              User Details
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                mt: 0.5,
              }}
            >
              View user information
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#6b7280',
            '&:hover': { bgcolor: '#f3f4f6' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* User Avatar + Info */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                bgcolor: '#f9fafb',
                borderRadius: 3,
                p: 3,
                mt: 2,
                border: '1px solid #e5e7eb',
              }}

              // sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}
            >
              <Avatar
                src={user.profileImageUrl}
                sx={{
                  width: 90,
                  height: 90,
                  bgcolor: userTypeStyle.bg,
                  color: userTypeStyle.color,
                  fontSize: '1.6rem',
                  fontWeight: 600,
                }}
              >
                {!user.profileImageUrl &&
                  getInitials(user.firstName, user.lastName)}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '1.6rem',
                    fontWeight: 700,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  {user.firstName} {user.lastName}
                </Typography>

                {/* Country Block */}
                {user.delegateDetails && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.7,
                      bgcolor: '#eef2ff',
                      color: '#3730a3',
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      mb: 1.5,
                    }}
                  >
                    {user.delegateDetails.country}
                  </Box>
                )}

                {/* Tags */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={
                      typeof user.role?.name === 'string'
                        ? user.role?.name
                        : user.role?.name || 'Unknown'
                    }
                    sx={{
                      bgcolor: userTypeStyle.bg,
                      color: userTypeStyle.color,
                      fontWeight: 600,
                      height: 26,
                    }}
                  />

                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    sx={{
                      bgcolor: statusStyle.bg,
                      color: statusStyle.color,
                      fontWeight: 600,
                      height: 26,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* --- Contact Info Card ---- */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <EmailIcon sx={{ fontSize: 22, color: '#6b7280', mt: 0.4 }} />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 0.5,
                      fontSize: '0.9rem',
                    }}
                  >
                    Email Address
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {user.email}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                    }}
                  ></Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* --- Phone Card ---- */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <PhoneIcon sx={{ fontSize: 22, color: '#6b7280', mt: 0.4 }} />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 0.5,
                      fontSize: '0.9rem',
                    }}
                  >
                    Phone Number
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {phoneNumber}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* --- User ID & Registration Source --- */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#fafafa',
                border: '1px solid #e5e7eb',
              }}
            >
              <Typography
                sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.8 }}
              >
                User ID
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#111827',
                }}
              >
                #{user.id.toString().padStart(6, '0')}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#eef9ff',
                border: '1px solid #bae6fd',
              }}
            >
              <Typography
                sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 0.8 }}
              >
                Registration Source
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#0c4a6e',
                  textTransform: 'capitalize',
                }}
              >
                {user.registrationSource}
              </Typography>
            </Box>
          </Grid>

          {/* --- Account Info --- */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '0.95rem',
                  color: '#374151',
                }}
              >
                Account Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Created On
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#111827' }}>
                    {formatDate(user.createdAt)}
                  </Typography>
                </Grid>

                {user.lastLoginAt && (
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Last Login
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#111827' }}>
                      {formatDate(user.lastLoginAt)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: '1px solid #e5e7eb',
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            bgcolor: '#3b82f6',
            '&:hover': {
              bgcolor: '#2563eb',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserViewDialog;
